import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { useAuthStore } from '@/stores/auth.store'
import { visitService } from '@/services/visit.service'
import { businessService } from '@/services/business.service'
import { referralService } from '@/services/referral.service'
import { configService } from '@/services/config.service'
import logoImg from '@/assets/logo-icon.png'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useConfirm } from '@/hooks/useConfirm'
import { offlineStorage, OfflineVisit } from '@/lib/offline-storage'
import { VisitOutcome } from '@shared/types/entities'

export function VisitDetailPage() {
  const { t } = useTranslation()
  const { id: visitId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isOnline = useOnlineStatus()
  const { confirm, modal: confirmModal } = useConfirm()

  const [notes, setNotes] = useState('')
  const [outcome, setOutcome] = useState<VisitOutcome>(VisitOutcome.INTERESTED)
  const [photos, setPhotos] = useState<File[]>([])
  const [uploadingPhotos, setUploadingPhotos] = useState(false)
  const [gpsStatus, setGpsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [endLocation, setEndLocation] = useState<{ latitude: number; longitude: number } | null>(null)
  const [offlineVisit, setOfflineVisit] = useState<OfflineVisit | null>(null)
  const [savedOffline, setSavedOffline] = useState(false)
  const [now, setNow] = useState(Date.now())

  const isOfflineVisit = visitId?.startsWith('offline-')

  // Load offline visit data if this is an offline visit
  useEffect(() => {
    if (isOfflineVisit && visitId) {
      offlineStorage.getVisit(visitId).then((v) => {
        if (v) setOfflineVisit(v)
      })
    }
  }, [visitId, isOfflineVisit])

  // Tick every second to update elapsed time
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  // Fetch visit details from server (only for online visits)
  const { data: visitData } = useQuery({
    queryKey: ['visit', visitId],
    queryFn: () => visitService.get(visitId!, token!),
    enabled: !!token && !!visitId && !isOfflineVisit && isOnline,
    select: (data: any) => data.visit,
  })

  const { data: referralData } = useQuery({
    queryKey: ['referral'],
    queryFn: () => referralService.getReferral(token!),
    enabled: !!token,
  })

  const { data: configData } = useQuery({
    queryKey: ['config', 'compensation'],
    queryFn: () => configService.getCompensationConfig(),
  })
  const maxDistanceMeters = configData?.maxDistanceMeters ?? 200

  const visit = isOfflineVisit ? offlineVisit : (visitData as any)
  const startTime = visit?.startTime

  const { data: currentBusiness } = useQuery({
    queryKey: ['business', visit?.businessId],
    queryFn: () => businessService.getBusiness(visit!.businessId, token!),
    enabled: !!token && isOnline && !!visit?.businessId,
    refetchInterval: (query) => {
      const biz = query.state.data as any
      return biz?.status === 'REGISTERED' ? false : 5000
    },
    refetchIntervalInBackground: true,
  })

  const ownerHasRegistered = (currentBusiness as any)?.status === 'REGISTERED'

  useEffect(() => {
    if (ownerHasRegistered) setOutcome(VisitOutcome.REGISTERED)
  }, [ownerHasRegistered])

  const frontendUrl = import.meta.env.VITE_FRONTEND_URL || window.location.origin
  const businessQrUrl = visit?.businessId && referralData?.referralCode
    ? `${frontendUrl}/merchant/register?ref=${referralData.referralCode}&business=${visit.businessId}`
    : null

  // Calculate duration (updates every second via `now` state)
  const duration = startTime
    ? Math.floor((now - new Date(startTime).getTime()) / 1000)
    : 0

  // Complete visit mutation (online)
  const completeVisitMutation = useMutation({
    mutationFn: async () => {
      const uploadedUrls: string[] = []

      if (photos.length > 0) {
        setUploadingPhotos(true)
        for (const photo of photos) {
          const { uploadUrl, photoUrl } = await visitService.getPhotoPresignedUrl(
            { visitId: visitId!, fileName: photo.name, contentType: photo.type },
            token!
          ) as any
          await visitService.uploadPhoto(uploadUrl, photo)
          uploadedUrls.push(photoUrl)
        }
        setUploadingPhotos(false)
      }

      return visitService.complete(
        visitId!,
        {
          endLatitude: endLocation!.latitude,
          endLongitude: endLocation!.longitude,
          notes,
          outcome,
          photoUrls: uploadedUrls,
        },
        token!
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-visits'] })
      navigate('/scout/dashboard')
    },
  })

  // Get GPS on mount
  useEffect(() => {
    if (gpsStatus === 'idle') {
      setGpsStatus('loading')

      if (!navigator.geolocation) {
        setGpsStatus('error')
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setEndLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          setGpsStatus('ready')
        },
        () => {
          setGpsStatus('error')
        }
      )
    }
  }, [gpsStatus])

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith('image/')) return false
      if (file.size > 5 * 1024 * 1024) return false
      return true
    })
    setPhotos([...photos, ...validFiles])
  }

  const handleRemovePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index))
  }

  const handleCompleteOffline = async () => {
    if (!visitId || !endLocation) return

    // Save photos to IndexedDB
    for (const photo of photos) {
      const photoId = `photo-${Date.now()}-${Math.random().toString(36).slice(2)}`
      await offlineStorage.savePhoto({
        id: photoId,
        visitId,
        blob: photo,
        fileName: photo.name,
        synced: false,
      })
    }

    // Update offline visit as completed
    const existingVisit = await offlineStorage.getVisit(visitId)
    if (existingVisit) {
      await offlineStorage.saveVisit({
        ...existingVisit,
        endTime: new Date().toISOString(),
        endLatitude: endLocation.latitude,
        endLongitude: endLocation.longitude,
        duration,
        notes,
        outcome,
        isCompleted: true,
        synced: false,
      })
    } else {
      // Online visit being completed offline
      await offlineStorage.saveVisit({
        id: visitId,
        businessId: visit?.businessId || '',
        startTime: visit?.startTime || new Date().toISOString(),
        startLatitude: visit?.startLatitude || 0,
        startLongitude: visit?.startLongitude || 0,
        endTime: new Date().toISOString(),
        endLatitude: endLocation.latitude,
        endLongitude: endLocation.longitude,
        duration,
        notes,
        outcome,
        isCompleted: true,
        synced: false,
        serverVisitId: visitId,
      })
    }

    setSavedOffline(true)
    setTimeout(() => navigate('/scout/dashboard'), 1500)
  }

  const handleComplete = async () => {
    if (!notes) {
      const ok = await confirm({
        title: t('scout.completeVisit'),
        message: t('scout.noNotesConfirm'),
        confirmLabel: t('scout.continueAnyway'),
        cancelLabel: t('common.cancel'),
      })
      if (!ok) return
    }
    if (photos.length === 0) {
      const ok = await confirm({
        title: t('scout.completeVisit'),
        message: t('scout.noPhotosConfirm'),
        confirmLabel: t('scout.continueAnyway'),
        cancelLabel: t('common.cancel'),
      })
      if (!ok) return
    }

    if (gpsStatus !== 'ready') return

    if (!isOnline || isOfflineVisit) {
      handleCompleteOffline()
    } else {
      completeVisitMutation.mutate()
    }
  }

  if (!visit && !isOfflineVisit) {
    return <div className="p-8 text-center">{t('common.loading')}</div>
  }

  // Read-only view for completed visits
  if (visit?.isCompleted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {visit.businessName || t('scout.visit')}
              </h1>
              <button
                onClick={() => navigate('/scout/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                &larr; {t('common.back')}
              </button>
            </div>

            <div className="space-y-4">
              {visit.rejected && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{t('scout.visitRejected')}</span>
                    {visit.rejectedAt && (
                      <span className="text-xs text-gray-500">{new Date(visit.rejectedAt).toLocaleDateString('de-DE')}</span>
                    )}
                  </div>
                  {visit.rejectionReason && (
                    <div className="text-sm text-red-800 mt-2">{visit.rejectionReason}</div>
                  )}
                  <p className="text-sm text-red-700 mt-2">{t('scout.visitRejectedHint')}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase">{t('admin.date')}</div>
                  <div className="font-medium">{new Date(visit.startTime).toLocaleDateString('de-DE')}</div>
                  <div className="text-sm text-gray-600">
                    {new Date(visit.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    {visit.endTime && ` – ${new Date(visit.endTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase">{t('admin.duration')}</div>
                  <div className="font-medium">
                    {visit.duration ? `${Math.floor(visit.duration / 60)} ${t('scout.minutes')}` : '-'}
                  </div>
                </div>
              </div>

              {visit.outcome && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase mb-1">{t('scout.visitOutcome')}</div>
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                    {t(`scout.outcome.${visit.outcome.toLowerCase()}`)}
                  </span>
                </div>
              )}

              {visit.notes && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase mb-1">{t('scout.visitNotes')}</div>
                  <div className="text-gray-900 whitespace-pre-wrap">{visit.notes}</div>
                </div>
              )}

              {visit.photoUrls && visit.photoUrls.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase mb-2">{t('scout.photos')}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {visit.photoUrls.map((url: string, i: number) => (
                      <img key={i} src={url} alt={`Photo ${i + 1}`} className="w-full h-32 object-cover rounded-lg" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (savedOffline) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-sm">
          <div className="text-4xl mb-4">&#x2713;</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('offline.visitSaved')}</h2>
          <p className="text-gray-600">{t('offline.willSyncLater')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('scout.completeVisit')}</h1>
          {visit?.businessName && (
            <p className="text-lg text-gray-700 mb-6">{visit.businessName}</p>
          )}

          {!isOnline && (
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              {t('offline.completeOfflineNote')}
            </div>
          )}

          {/* Visit Info */}
          {startTime && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">
                {t('scout.startedAt')}: {new Date(startTime).toLocaleString()}
              </div>
              <div className="text-sm font-mono text-gray-800">
                {String(Math.floor(duration / 3600)).padStart(2, '0')}:{String(Math.floor((duration % 3600) / 60)).padStart(2, '0')}:{String(duration % 60).padStart(2, '0')}
              </div>
            </div>
          )}

          {/* Distance Info */}
          {visit?.distanceMeters !== undefined && (
            <div className={`mb-6 p-4 border rounded-lg ${
              visit.distanceMeters > maxDistanceMeters
                ? 'bg-red-50 border-red-300'
                : 'bg-green-50 border-green-200'
            }`}>
              <p className={`text-sm font-medium ${visit.distanceMeters > maxDistanceMeters ? 'text-red-800' : 'text-green-800'}`}>
                {t('scout.distanceAtStart', { distance: visit.distanceMeters })}
                {visit.distanceOverridden && (
                  <span className="ml-2 text-xs">({t('scout.overridden')})</span>
                )}
              </p>
              {visit.distanceMeters > maxDistanceMeters && (
                <p className="text-sm text-red-700 mt-1">
                  {t('scout.distanceTooFarToComplete', { maxDistance: maxDistanceMeters })}
                </p>
              )}
            </div>
          )}

          {/* QR Code for merchant */}
          {businessQrUrl && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg text-center">
              <h3 className="text-sm font-semibold text-purple-800 mb-2">
                {t('referral.qrCodeDescription')}
              </h3>
              <div className="inline-block bg-white p-3 rounded-lg">
                <QRCodeSVG
                  value={businessQrUrl}
                  size={200}
                  level="H"
                  includeMargin
                  imageSettings={{
                    src: logoImg,
                    x: undefined,
                    y: undefined,
                    height: 40,
                    width: 40,
                    excavate: true,
                  }}
                />
              </div>
              <p className="text-xs text-purple-600 mt-2">{visit?.businessName}</p>
              <p className="text-xs text-purple-500">{referralData?.referralCode}</p>
            </div>
          )}

          {/* GPS Status */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('scout.gpsLocation')}
            </label>
            <div
              className={`p-3 rounded-lg text-sm ${
                gpsStatus === 'ready'
                  ? 'bg-green-50 text-green-700'
                  : gpsStatus === 'error'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-gray-50 text-gray-700'
              }`}
            >
              {gpsStatus === 'loading' && t('scout.gettingLocation')}
              {gpsStatus === 'ready' && `${t('scout.positionCaptured')}`}
              {gpsStatus === 'error' && `${t('scout.gpsError')}`}
            </div>
          </div>

          {/* Owner registered notification */}
          {ownerHasRegistered && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <span className="text-green-500 text-2xl leading-none">&#10003;</span>
              <div>
                <p className="font-semibold text-green-800">{t('scout.ownerRegisteredTitle')}</p>
                <p className="text-sm text-green-700 mt-1">{t('scout.ownerRegisteredMessage')}</p>
              </div>
            </div>
          )}

          {/* Outcome */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('scout.visitOutcome')} *
            </label>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as VisitOutcome)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value={VisitOutcome.INTERESTED}>{t('scout.outcome.interested')}</option>
              <option value={VisitOutcome.NOT_INTERESTED}>{t('scout.outcome.notInterested')}</option>
              <option value={VisitOutcome.CALLBACK_REQUIRED}>{t('scout.outcome.callbackRequired')}</option>
              <option value={VisitOutcome.REGISTERED}>{t('scout.outcome.registered')}</option>
              <option value={VisitOutcome.CLOSED}>{t('scout.outcome.closed')}</option>
              <option value={VisitOutcome.NOT_FOUND}>{t('scout.outcome.notFound')}</option>
            </select>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('scout.visitNotes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('scout.notesPlaceholder')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Photos */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('scout.photos')}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handlePhotoSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('scout.takePhoto')}
            </button>

            {photos.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={() => navigate(-1)}
              disabled={completeVisitMutation.isPending || uploadingPhotos}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleComplete}
              disabled={gpsStatus !== 'ready' || completeVisitMutation.isPending || uploadingPhotos || (visit?.distanceMeters !== undefined && visit.distanceMeters > maxDistanceMeters && !visit.distanceOverridden)}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {uploadingPhotos
                ? t('scout.uploadingPhotos')
                : completeVisitMutation.isPending
                ? t('common.loading')
                : t('scout.completeVisit')}
            </button>
          </div>
        </div>
      </div>
      {confirmModal}
    </div>
  )
}
